import { useState } from "react";
import { Button, DatePicker, Spin, message } from "antd";
import { Column, Line } from "@ant-design/charts";
import dayjs from "dayjs";
import { fetchRetentionHeatMap, fetchRetentionStat } from "../../apis/statisticsApi";
import { fetchNewUsers } from "../../apis/usersApi";
import NewUserList from "../../components/NewUserList";
import RetentionHeatmap from "../../components/statistics/RetentionHeatmap";
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const Retention = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [stackedData, setStackedData] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const [retentionHeatmap, setRetentionHeatMap] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    if (!startDate || !endDate) {
      message.warning("Por favor selecciona ambas fechas.");
      return;
    }
    if (!dayjs.utc(startDate).isBefore(dayjs.utc(endDate), "day")) {
      message.warning("La fecha de inicio debe ser anterior a la fecha de fin.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetchRetentionStat(
        dayjs.utc(startDate).format("YYYY-MM-DD"),
        dayjs.utc(endDate).format("YYYY-MM-DD")
      );

      const stack = res.data.flatMap((d) => {
        const month = dayjs.utc(d.month_start).format("YYYY-MM");
        return [
          { month, type: "Start", value: d.customers_start },
          { month, type: "New", value: d.new_customers },
          { month, type: "Revived", value: d.revived_customers },
        ];
      });

      const line = res.data.map((d) => ({
        month: dayjs.utc(d.month_start).format("YYYY-MM"),
        retention_rate: d.retention_rate * 100,
      }));

      setStackedData(stack);
      setLineData(line);
    } catch (err) {
      console.error("Error fetching retention stats:", err);
      message.error("Error al cargar estadísticas");
    }
    try {
      const res = await fetchNewUsers(
        dayjs.utc(startDate).format("YYYY-MM-DD"),
        dayjs.utc(endDate).format("YYYY-MM-DD")
      );

      setNewUsers(res.data);
    } catch (err) {
      console.error("Error fetching retention stats:", err);
      message.error("Error al cargar estadísticas");
    }
    try {
      const res = await fetchRetentionHeatMap(
        dayjs.utc(startDate).format("YYYY-MM-DD"),
        dayjs.utc(endDate).format("YYYY-MM-DD")
      );

      setRetentionHeatMap(res.data);
    } catch (err) {
      console.error("Error fetching retention heat map:", err);
      message.error("Error al cargar estadísticas");
    }
    setLoading(false);
  };

  
  const columnConfig = {
    data: stackedData,
    xField: "month",
    yField: "value",
    seriesField: "type",
    colorField: "type",
    label: {
      text: 'value',
      textBaseline: 'bottom',
      position: 'inside',
    },
    interaction: {
      elementHighlightByColor: {
        link: true,
      },
    },
    state: {
      active: { linkFill: 'rgba(0,0,0,0.25)', stroke: 'black', lineWidth: 0.5 },
      inactive: { opacity: 0.5 },
    },
  };

  const lineConfig = {
    data: lineData,
    xField: "month",
    yField: "retention_rate",
    smooth: true,
    yAxis: {
      label: { formatter: (v) => `${v}%` },
    },
  };

  return (
    <div className="container">
      <h2 className="text-center mb-3">Retención</h2>
      <div className="text-center" style={{ marginBottom: 16 }}>
        <DatePicker onChange={(d) => setStartDate(d)} />{" "}
        <DatePicker onChange={(d) => setEndDate(d)} />{" "}
        <Button onClick={fetchStats} type="primary">
          Buscar
        </Button>
      </div>
      {loading ? (
        <Spin />
      ) : (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-2xl shadow">
              <h2 className="text-lg font-bold mb-2">Customer Distribution</h2>
              <Column {...columnConfig} />
            </div>
            <div className="bg-white p-4 rounded-2xl shadow">
              <h2 className="text-lg font-bold mb-2">Retention Rate</h2>
              <Line {...lineConfig} />
            </div>
            <div className="bg-white p-4 rounded-2xl shadow">
              <h2 className="text-lg font-bold mb-2">Nuevos usuarios</h2>
              <NewUserList users={newUsers} />
            </div>
            <div className="bg-white p-4 rounded-2xl shadow">
              <h2 className="text-lg font-bold mb-2">Retention HeatMap</h2>
              <RetentionHeatmap data={retentionHeatmap} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Retention;
